import React, { useMemo } from 'react';
import { GitCommitHorizontal, Calendar, Layers, Flag } from 'lucide-react';
import { CampaignPost } from '../types/calendar';

interface TimelineViewProps {
  posts: CampaignPost[];
  onSelectPost: (post: CampaignPost) => void;
}

export const TimelineView: React.FC<TimelineViewProps> = ({ posts, onSelectPost }) => {
  // Sort posts chronologically by rawDate
  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => {
      const dateA = a.rawDate ? a.rawDate.getTime() : 0;
      const dateB = b.rawDate ? b.rawDate.getTime() : 0;
      return dateA - dateB;
    });
  }, [posts]);

  // Group posts by Phase -> Week
  const phaseMap = useMemo(() => {
    const map: Record<string, Record<string, CampaignPost[]>> = {};

    sortedPosts.forEach(post => {
      const phase = post.phase || 'General Phase';
      const week = post.week || 'Unassigned';

      if (!map[phase]) map[phase] = {};
      if (!map[phase][week]) map[phase][week] = [];

      map[phase][week].push(post);
    });

    return map;
  }, [sortedPosts]);

  const getPlatformClass = (platform: string) => {
    const p = platform.toLowerCase();
    if (p.includes('instagram')) return 'platform-instagram';
    if (p.includes('facebook')) return 'platform-facebook';
    if (p.includes('tiktok')) return 'platform-tiktok';
    if (p.includes('pinterest')) return 'platform-pinterest';
    if (p.includes('youtube')) return 'platform-youtube';
    return '';
  };

  return (
    <div style={{
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem',
      flex: 1,
    }}>
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '1.25rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '10px',
            background: 'var(--accent-primary)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <GitCommitHorizontal size={24} />
          </div>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.15rem', fontWeight: 700 }}>
              Campaign Roadmap Timeline
            </h2>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Chronological campaign execution view organized by Campaign Phase & Week
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {Object.keys(phaseMap).map(phase => (
            <div key={phase} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-primary)' }} />
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{phase}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Phases and Weeks Roadmap */}
      {Object.keys(phaseMap).length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          No posts available to render timeline.
        </div>
      ) : (
        Object.entries(phaseMap).map(([phaseName, weeksObj]) => (
          <div key={phaseName} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            
            {/* Phase Section Title */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              borderBottom: '2px solid var(--accent-primary)',
              paddingBottom: '0.5rem',
            }}>
              <Flag size={18} style={{ color: 'var(--accent-primary)' }} />
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.2rem',
                fontWeight: 700,
                color: 'var(--text-main)',
              }}>
                {phaseName}
              </h3>
            </div>

            {/* Weeks Container */}
            {Object.entries(weeksObj).map(([weekName, weekPosts]) => (
              <div key={weekName} style={{
                position: 'relative',
                paddingLeft: '2rem',
                borderLeft: '2px dashed var(--border-color)',
                marginLeft: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.85rem',
              }}>
                {/* Timeline Dot & Week Name */}
                <div style={{
                  position: 'absolute',
                  left: '-9px',
                  top: '0',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: 'var(--accent-primary)',
                  border: '3px solid var(--bg-primary)',
                }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-main)' }}>
                    {weekName}
                  </span>
                  <span className="badge" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                    {weekPosts.length} posts
                  </span>
                </div>

                {/* Posts Cards Grid for Week */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '0.85rem',
                }}>
                  {weekPosts.map(post => (
                    <div
                      key={post.postId}
                      onClick={() => onSelectPost(post)}
                      style={{
                        padding: '0.85rem 1rem',
                        borderRadius: '10px',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-color)',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span className={`platform-badge ${getPlatformClass(post.platform)}`}>
                          {post.platform}
                        </span>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-light)' }}>
                          {post.date} ({post.day})
                        </span>
                      </div>

                      <div style={{
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: 'var(--text-main)',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {post.hook || post.contentPillar || 'Post Detail'}
                      </div>

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '0.25rem',
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                      }}>
                        <span>{post.postId}</span>
                        {post.format && (
                          <span style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', background: 'var(--bg-primary)' }}>
                            {post.format}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
};
